import {
  DependencyList,
  useEffect,
  useState
} from 'react';

type UseAsyncResult<TValue> = [
  // Last value or null if none have been fetched.
  TValue | null,
  // Whether the response is being loaded.
  boolean,
  // Error thrown while fetching.
  unknown
];

/** Caches an async response based on @see dependencies. */
export function useAsync<TArgs extends DependencyList, TValue>(
  factory: () => Promise<TValue> | TValue,
  dependencies: TArgs
): UseAsyncResult<TValue> {
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [value, setValue] = useState<TValue | null>(null);

  useEffect(() => {
    let isLatest = true;
    (async () => {
      setIsLoading(true);
      try {
        const newValue = await factory();
        if (isLatest) {
          // Functional updater: `TValue` itself may be a function.
          setValue(() => newValue);
          setError(null);
        }
      } catch (newError) {
        console.error('useAsync error:', newError);
        if (isLatest) {
          setValue(null);
          setError(() => newError);
        }
      }

      if (isLatest) {
        setIsLoading(false);
      }
    })();

    return () => {
      isLatest = false;
    };
  }, dependencies);

  return [value, isLoading, error];
}
